import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import Divider from '@mui/material/Divider';
import Dialog from '../../../../components/dialog';
import CSVRecord from '../../components/csv-record';
import DataLoader from '../../../../components/data-loader';
import FileUploader, { FILE_TYPE } from '../../../../components/file-uploader/index';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { STATUS } from '../csv-users-upload/index';
import { CSV_MAX_USERS } from '../csv-users-upload/constants';
import { FILE_STATUSES } from '../../api/types';
import { getCSVUsersInfo } from '../csv-users-upload/utils';
import { CSVUsersInfo } from '../csv-users-upload/types';
import {
    useGetImportUsersStatusQuery,
    useGetUsersTemplateQuery,
    useImportUsersFileMutation,
} from '../../api/userManagementApi';
import { useGetTenantProductsQuery } from '../../../../features/products/api/productsApi';
import { useTranslation } from 'react-i18next';
import Button from '../../../../components/button';
import Multiselect from '../../../../components/multiselect';
import { Product } from '../../../products/api/types';

interface CSVUsersUploadDialogProps {
    onModalClose: () => void;
    isModalShown: boolean;
}

const CSVUsersUploadDialog: React.FC<CSVUsersUploadDialogProps> = ({ onModalClose, isModalShown }) => {
    const { t } = useTranslation();

    const [errorMessage, setErrorMessage] = useState('');
    const [jobId, setJobId] = useState<string>('');
    const [fileStatus, updateFileStatus] = useState<(typeof STATUS)[keyof typeof STATUS]>(STATUS.IDLE);
    const [fileInfoData, setFileInfoData] = useState<CSVUsersInfo | null>(null);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [productSearch, setProductSearch] = useState('');

    const { data, status, error } = useGetImportUsersStatusQuery(
        { jobId },
        {
            pollingInterval: 5000,
            skip: fileStatus === STATUS.COMPLETED || fileStatus === STATUS.IDLE || !jobId,
            refetchOnMountOrArgChange: true,
        }
    );
    const { data: templateData } = useGetUsersTemplateQuery();
    const { data: products } = useGetTenantProductsQuery();

    const [importUsersFile] = useImportUsersFileMutation();

    const clearCSVData = (clearError = true, clearFile = true) => {
        updateFileStatus(STATUS.IDLE);
        setJobId('');

        if (clearFile) setFileInfoData(null);
        if (clearError) setErrorMessage('');
    };

    useEffect(() => {
        if (jobId && (fileStatus === STATUS.LOADING || fileStatus === STATUS.FINALIZING)) {
            if (data?.status === FILE_STATUSES.RUNNING) {
                updateFileStatus(STATUS.FINALIZING);
            }

            if (error || data?.status === FILE_STATUSES.FAILED) {
                clearCSVData(false);
                setErrorMessage(t('users-upload-csv-error'));
            }

            if (data?.status === FILE_STATUSES.COMPLETED) {
                setJobId('');
                setFileInfoData(getCSVUsersInfo(data));
                updateFileStatus(STATUS.COMPLETED);
            }
        }
    }, [data, status, error, fileStatus, t, fileInfoData, jobId]);

    const filteredProducts = useMemo(() => {
        return products?.filter((product) => product.shortName.toLowerCase().includes(productSearch.toLowerCase()));
    }, [productSearch, products]);

    const onCloseModal = () => {
        onModalClose();
    };

    const handleDownload = () => {
        if (templateData) {
            const blob = new Blob([templateData], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'users-template.csv';
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    const uploadCSV = (file: File | null) => {
        clearCSVData();

        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const csvData = reader.result as string;
                const rows = csvData.split('\n');
                const users = rows.slice(1); // skip the header row
                if (users.length - 1 > CSV_MAX_USERS) {
                    setErrorMessage(t('users-upload-csv-error'));
                    return;
                }
                setErrorMessage('');

                const formData = new FormData();
                formData.append('file', file);

                selectedProducts.forEach((product, index) => {
                    formData.append(`products[${index}].shortName`, product.shortName);
                    formData.append(`products[${index}].manufacturerName`, product.manufacturerName);
                });

                updateFileStatus(STATUS.LOADING);

                importUsersFile(formData)
                    .unwrap()
                    .then((data) => {
                        if (data?.jobId) {
                            setJobId(data.jobId);
                        }

                        return Promise.resolve(data);
                    })
                    .catch(() => {
                        clearCSVData(false);
                        setErrorMessage(t('users-upload-csv-error'));
                    });
            };
            reader.readAsText(file);
        }
    };

    const handleProductChange = (event: React.ChangeEvent<HTMLInputElement>, item: Product) => {
        setSelectedProducts((prev) => {
            const newValue = event.target.checked
                ? [...prev, item]
                : prev.filter((product) => product.shortName !== item.shortName);

            return newValue;
        });
    };

    const handleProductSearchChange = (value: string) => {
        setProductSearch(value);
    };

    const clearAllProducts = () => {
        setSelectedProducts((prev) => {
            const newValue =
                prev.length > 0
                    ? prev.filter((product) => !product.shortName.toLowerCase().includes(productSearch.toLowerCase()))
                    : [];

            return newValue;
        });
    };

    const selectAllProducts = () => {
        setSelectedProducts((prev) => {
            const filteredNotSelected = filteredProducts?.filter(
                (product) => !prev.some((value) => value.shortName === product.shortName)
            );

            return [...(prev || []), ...(filteredNotSelected || [])];
        });
    };

    return (
        <Dialog isOpen={isModalShown} title={t('csv-users-upload')} onClose={onCloseModal} fullScreenOnMobile>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    flexDirection: 'column',
                    width: '504px',
                    '@media (max-width: 600px)': {
                        width: 'calc(100vw - 75px)',
                    },
                }}
            >
                {fileStatus === STATUS.COMPLETED && fileInfoData ? (
                    <Box>
                        <Box>
                            {!!fileInfoData.succeedCount && (
                                <Typography sx={{ color: 'common.system.success.main', fontSize: '14px', mb: 1 }}>
                                    {fileInfoData.succeedCount} {t('users-uploaded-message')}
                                </Typography>
                            )}
                            {!!fileInfoData.conflictCount && (
                                <Typography sx={{ color: 'common.system.error.main', fontSize: '14px', mb: 1 }}>
                                    {fileInfoData.conflictCount} {t('duplicated')}
                                </Typography>
                            )}
                            {!!fileInfoData.invalidCount && (
                                <Typography sx={{ color: 'common.system.error.main', fontSize: '14px', mb: '10px' }}>
                                    {fileInfoData.invalidCount} {t('failed')}
                                </Typography>
                            )}
                        </Box>

                        {fileInfoData.records.length > 0 && <Divider />}

                        <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                            {fileInfoData.records.map((record, index) => (
                                <CSVRecord key={index} {...record} />
                            ))}
                        </Box>

                        <Divider />

                        <Box sx={{ display: 'flex' }}>
                            <Button
                                size='large'
                                variant='primary'
                                type='submit'
                                sx={{ margin: '12px 0 0 auto' }}
                                onClick={onCloseModal}
                                data-testid='close-button'
                            >
                                {t('finish')}
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <>
                        {filteredProducts && (
                            <Box
                                sx={{
                                    marginBottom: '24px',
                                }}
                            >
                                <Multiselect<Product>
                                    selectedItems={selectedProducts}
                                    items={filteredProducts}
                                    fetchMoreData={() => null}
                                    renderLabel={(product) => `${product.shortName}`}
                                    itemIdentifier={(product) => `${product.shortName}`}
                                    handleItemChange={handleProductChange}
                                    label={t('assign-products')}
                                    id={'drugsScrollableDiv'}
                                    searchPlaceholder={t('search-for-drugs')}
                                    search={productSearch}
                                    handleSearchChange={handleProductSearchChange}
                                    clearAll={clearAllProducts}
                                    selectAll={selectAllProducts}
                                    disabled={fileStatus === STATUS.LOADING || fileStatus === STATUS.FINALIZING}
                                />
                            </Box>
                        )}

                        <DataLoader
                            isLoading={fileStatus === STATUS.LOADING || fileStatus === STATUS.FINALIZING}
                            loadingMessage={
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        maxWidth: '340px',
                                        margin: '16px auto 0',
                                        color: 'common.grey1',
                                        textAlign: 'center',
                                    }}
                                >
                                    {fileStatus === STATUS.LOADING && t('csv-validation-progress')}
                                    {fileStatus === STATUS.FINALIZING && t('users-csv-loading-message')}
                                </Typography>
                            }
                            sx={{
                                height: '280px',
                                position: 'relative',
                                border: '1px dashed',
                                borderColor: 'common.primary',
                                borderRadius: '4px',
                            }}
                        >
                            <FileUploader
                                type={FILE_TYPE.CSV}
                                handleFileUpload={uploadCSV}
                                externalErrorMessage={errorMessage}
                                secondaryText={t('users-csv-secondary-text')}
                                sx={{ height: '280px' }}
                            />
                        </DataLoader>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                borderRadius: '4px',
                                border: '1px solid',
                                borderColor: 'common.grey3',
                                fontSize: '14px',
                                marginTop: '24px',
                                padding: '16px',
                            }}
                        >
                            <Box>
                                <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
                                    {t('sample-form-option')}
                                </Typography>
                                <Typography sx={{ color: 'common.grey1', fontSize: '14px', lineHeight: '20px' }}>
                                    {t('users-csv-sample-form-description')}
                                </Typography>
                            </Box>
                            <IconButton
                                sx={{ padding: 0, marginLeft: '16px' }}
                                onClick={handleDownload}
                                data-testid='download-icon'
                            >
                                <FileDownloadOutlinedIcon
                                    sx={{
                                        color: 'common.primary',
                                        fontSize: '24px',
                                        alignSelf: 'baseline',
                                    }}
                                />
                            </IconButton>
                        </Box>
                    </>
                )}
            </Box>
        </Dialog>
    );
};

export default CSVUsersUploadDialog;
